import { inject, NgModule } from '@angular/core';
import { Router, RouterModule, Routes } from '@angular/router';

import { LoginComponent } from './authetification/login/login.component';
import { combineLatest, take, map } from 'rxjs';
import { AuthService } from './service/auth.service';
import { WoundEvaluationGuard } from './guards/wound-evaluation.guard';
import { environment } from '../environments/environment';
import { hasPartnerRole } from './core/workspace-role';
import {isPartnerSession} from './external-partner/external-partner-routing';
import { RoleRedirectGuard } from './authentification/role-redirect.guard';
import { telehealthFieldGuard, telehealthProviderGuard } from './guards/telehealth-role.guard';

const authGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.user$.pipe(take(1), map(u => (u ? true : router.createUrlTree(['/login']))));
};

const internalWorkspaceGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles => hasPartnerRole(roles) ? router.createUrlTree(['/partner/dashboard']) : true));
};

const mfaGuard = (_route: unknown, state: { url: string }) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return combineLatest([auth.mfaStatus$, auth.roles$]).pipe(take(1), map(([status, roles]) => {
    if (hasPartnerRole(roles) && !state.url.startsWith('/partner')) return router.createUrlTree(['/partner/dashboard']);
    if (!status.requireMfa) return true;
    if (!status.mfaEnrolled) {
      return status.mfaGraceGranted ? true :
        router.createUrlTree(['/mfa/enroll'], { queryParams: { next: state.url } });
    }
    if (!status.mfaPassed) {
      return router.createUrlTree(['/mfa/challenge'], { queryParams: { next: state.url } });
    }
    return true;
  }));
};

const schedulerGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles => {
    const allowed = ['super_admin', 'org_admin', 'admin', 'clinical_admin', 'hr', 'scheduler', 'employer', 'frontdesk', 'reception', 'receptionist'];
    const ok = roles.some(role => allowed.includes(role));
    return ok ? true : router.createUrlTree(['/forbidden']);
  }));
};

// PHWC operational workspaces are intentionally separate from facility
// wound-round leadership. DON / report-only wound_nurse users stay in the
// facility-scoped Wound Round experience and do not enter Scheduling/Tasks.
const operationalWorkflowGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles => {
    const allowed = [
      'super_admin', 'org_admin', 'admin', 'clinical_admin',
      'provider', 'np', 'nurse', 'employee', 'employer',
      'hr', 'scheduler', 'frontdesk', 'reception', 'receptionist'
    ];
    return roles.some(role => allowed.includes(role)) ? true : router.createUrlTree(['/forbidden']);
  }));
};

const clinicalIntakeGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles => {
    const allowed = ['super_admin', 'org_admin', 'admin', 'clinical_admin', 'provider', 'np', 'nurse', 'employer'];
    return roles.some(role => allowed.includes(role)) ? true : router.createUrlTree(['/forbidden']);
  }));
};

const clinicalReportGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles => {
    const allowed = ['super_admin', 'org_admin', 'admin', 'clinical_admin', 'provider', 'np', 'nurse'];
    return roles.some(role => allowed.includes(role)) ? true : router.createUrlTree(['/forbidden']);
  }));
};

const billingGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles => {
    const allowed = ['super_admin', 'org_admin', 'admin', 'clinical_admin', 'provider', 'np', 'billing', 'finance'];
    return roles.some(role => allowed.includes(role)) ? true : router.createUrlTree(['/forbidden']);
  }));
};

const telehealthPatientGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles =>
    roles.includes('patient') ? true : router.createUrlTree(['/forbidden'])
  ));
};

const telehealthLinkAdminGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles => {
    const allowed = ['clinical_admin', 'org_admin', 'admin', 'super_admin'];
    return roles.some(role => allowed.includes(role)) ? true : router.createUrlTree(['/forbidden']);
  }));
};

const externalPartnerGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles => {
    const allowed = ['billing_partner', 'credentialing_partner', 'supply_partner'];
    return roles.some(role => allowed.includes(role)) ? true : router.createUrlTree(['/forbidden']);
  }));
};

const partnerPatientWorkspaceGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.claims$.pipe(take(1), map(claims=>['billing','credentialing'].includes(String(claims['externalAccessType'])) ? true : router.createUrlTree(['/partner'])));
};

// A partner cannot enter an internal shell even when a legacy profile retains an internal role.
const partnerIsolationGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.claims$.pipe(take(1),map(claims=>isPartnerSession(claims) ? router.createUrlTree(['/partner']) : true));
};

const profitabilityGuard = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  return auth.roles$.pipe(take(1), map(roles => {
    const allowed = ['super_admin', 'org_admin', 'admin', 'clinical_admin', 'provider', 'np', 'nurse', 'billing', 'finance'];
    return roles.some(role => allowed.includes(role)) ? true : router.createUrlTree(['/forbidden']);
  }));
};

const routes: Routes = [
  ...(environment.production || environment.firebase.projectId !== 'jade-dev-5b0e3' ? [] : [{
    path: 'telehealth-dev-smoke',
    loadComponent: () => import('./modules/provider/pages/telehealth/telehealth-dev-smoke.component').then(m => m.TelehealthDevSmokeComponent),
  }]),
  ...(!['jade-dev-5b0e3', 'woundapp-261e6'].includes(environment.firebase.projectId) ? [] : [{
    path: 'telehealth', redirectTo: 'telehealth-dev', pathMatch: 'full' as const,
  }, {
    path: 'telehealth-dev',
    canActivate: [authGuard, mfaGuard],
    loadComponent: () => import('./modules/provider/pages/telehealth/telehealth-access-dev.component').then(m => m.TelehealthAccessDevComponent),
  }, {
    path: 'telehealth-dev/field-consult',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, telehealthFieldGuard],
    loadComponent: () => import('./modules/provider/pages/telehealth/telehealth-field-consult-dev.component').then(m => m.TelehealthFieldConsultDevComponent),
  }, {
    path: 'telehealth-dev/provider-inbox',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, telehealthProviderGuard],
    loadComponent: () => import('./modules/provider/pages/telehealth/telehealth-provider-inbox-dev.component').then(m => m.TelehealthProviderInboxDevComponent),
  }, {
    path: 'telehealth-dev/patient-portal',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, telehealthPatientGuard],
    loadComponent: () => import('./modules/provider/pages/telehealth/telehealth-patient-portal-dev.component').then(m => m.TelehealthPatientPortalDevComponent),
  }, {
    path: 'telehealth-dev/session/:appointmentId',
    canActivate: [authGuard, mfaGuard],
    loadComponent: () => import('./modules/provider/pages/telehealth/telehealth-session.component').then(m => m.TelehealthSessionComponent),
  }, {
    path: 'telehealth-dev/patient-link',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, telehealthLinkAdminGuard],
    loadComponent: () => import('./modules/provider/pages/telehealth/telehealth-patient-link-dev.component').then(m => m.TelehealthPatientLinkDevComponent),
  }, {
    path: 'telehealth-dev/billing-review',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, billingGuard],
    loadComponent: () => import('./modules/provider/pages/telehealth/telehealth-billing-review-dev.component').then(m => m.TelehealthBillingReviewDevComponent),
  }]),
  {
    path: 'innovacare-clinical',
    loadComponent: () => import('./public/innovacare-clinical/innovacare-clinical-page.component').then(m => m.InnovacareClinicalPageComponent),
    children: [
      { path: '', pathMatch: 'full', loadComponent: () => import('./public/innovacare-clinical/pages/innovacare-clinical-home-page.component').then(m => m.InnovacareClinicalHomePageComponent) },
      { path: 'services', loadComponent: () => import('./public/innovacare-clinical/pages/innovacare-clinical-services-page.component').then(m => m.InnovacareClinicalServicesPageComponent) },
      { path: 'portal', loadComponent: () => import('./public/innovacare-clinical/pages/innovacare-clinical-portal-page.component').then(m => m.InnovacareClinicalPortalPageComponent) },
      { path: 'education', loadComponent: () => import('./public/innovacare-clinical/pages/innovacare-clinical-education-page.component').then(m => m.InnovacareClinicalEducationPageComponent) },
      { path: 'contact', loadComponent: () => import('./public/innovacare-clinical/pages/innovacare-clinical-contact-page.component').then(m => m.InnovacareClinicalContactPageComponent) }
    ]
  },
  { path: 'login', component: LoginComponent },
  { path: 'session-expired', loadComponent: () => import('./authetification/session-expired/session-expired.component').then(m => m.SessionExpiredComponent) },
  // Backwards-compatible landing route for old bookmarks and legacy login
  // flows. The guard resolves the authenticated user's canonical Shell.
  { path: 'home', canActivate: [RoleRedirectGuard], component: LoginComponent },
  {
    path: 'demo',
    loadComponent: () => import('./features/demo/demo-launch.component').then(m => m.DemoLaunchComponent),
  },
  {
    path: 'mfa/enroll',
    canActivate: [authGuard],
    loadComponent: () => import('./authetification/mfa-enroll/mfa-enroll.component').then(m => m.MfaEnrollComponent),
  },
  {
    path: 'mfa/challenge',
    canActivate: [authGuard],
    loadComponent: () => import('./authetification/mfa-challenge/mfa-challenge.component').then(m => m.MfaChallengeComponent),
  },
  { path: '', redirectTo: 'innovacare-clinical', pathMatch: 'full' },
  { path: 'emar', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/emar/emar.module').then(m => m.EmarModule) },
  { path: 'mds', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/mds/mds.module').then(m => m.MdsModule) },
  { path: 'care-plans', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/care-plans/care-plans.module').then(m => m.CarePlansModule) },
  { path: 'orders', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/orders/orders.module').then(m => m.OrdersModule) },
  { path: 'education', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/education/education.module').then(m => m.EducationModule) },
  { path: 'social', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/social/social.module').then(m => m.SocialModule) },
  { path: 'media', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/media/media.module').then(m => m.MediaModule) },
  { path: 'predictive', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/predictive/predictive.module').then(m => m.PredictiveModule) },
  { path: 'infection-control', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/infection-control/infection-control.module').then(m => m.InfectionControlModule) },
  { path: 'risk-management', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/risk-managment/risk-managment.module').then(m => m.RiskManagmentModule) },
  { path: 'patients', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./patients/patients.module').then(m => m.PatientsModule) },
  {
    path: 'skin-wound',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, WoundEvaluationGuard],
    loadChildren: () => import('./features/skin-wound/skin-wound.module').then(m => m.SkinWoundModule),
  },
  {
    path: 'medicare-intake',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, clinicalIntakeGuard],
    loadComponent: () => import('./features/medicare-intake/medicare-intake-workspace.component').then(m => m.MedicareIntakeWorkspaceComponent),
  },
  {
    path: 'clinical-reports',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, clinicalReportGuard],
    loadComponent: () => import('./reports/pages/clinical-report-builder.page').then(m => m.ClinicalReportBuilderPage),
  },
  {
    path: 'claims',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, billingGuard],
    loadComponent: () => import('./billing/pages/medicare-claim-workspace.page').then(m => m.MedicareClaimWorkspacePage),
  },
  {
    path: 'claims/queue',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, billingGuard],
    loadComponent: () => import('./billing/pages/claims-queue.page').then(m => m.ClaimsQueuePage),
  },
  { path: 'medicare-claims', pathMatch: 'full', redirectTo: 'claims' },
  { path: 'billing', canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, billingGuard], children: [
    { path: '', pathMatch: 'full', redirectTo: 'queue' },
    { path: 'queue', loadComponent: () => import('./billing/pages/claims-queue.page').then(m => m.ClaimsQueuePage) },
    { path: 'claims', loadComponent: () => import('./billing/pages/medicare-claim-workspace.page').then(m => m.MedicareClaimWorkspacePage) },
    { path: 'clinical-handoff', loadComponent: () => import('./billing/pages/clinical-billing-handoff.page').then(m => m.ClinicalBillingHandoffPage) },
  ] },
  { path: 'partner', canActivate: [authGuard, mfaGuard, externalPartnerGuard], children: [
    { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
    { path: 'clinical-packages',
      loadComponent: () => import('./external-partner/external-partner-shell.component').then(m => m.ExternalPartnerShellComponent),
      children: [{ path: '', loadComponent: () => import('./external-partner/partner-clinical-packages.component').then(m => m.PartnerClinicalPackagesComponent) }] },
    ...[
      {path:'intake',view:'intake'},
      {path:'records',view:'records'},
      {path:'chart-documents',view:'documents'},
      {path:'registered-patients',view:'patients'},
      {path:'registered-patients/:patientId',view:'details'},
    ].map(item=>({
      path:item.path,canActivate:[partnerPatientWorkspaceGuard],
      loadComponent:()=>import('./external-partner/external-partner-shell.component').then(m=>m.ExternalPartnerShellComponent),
      children:[{path:'',data:{view:item.view},loadComponent:()=>import('./external-partner/external-partner-patient-workspace.component').then(m=>m.ExternalPartnerPatientWorkspaceComponent)}],
    })),
    ...['dashboard', 'work', 'patients', 'patients/:id', 'claims', 'claims/:id', 'documents', 'activity', 'providers', 'orders'].map(path => ({
      path, loadComponent: () => import('./external-partner/external-partner-dashboard.component').then(m => m.ExternalPartnerDashboardComponent),
    })),
  ] },
  {
    path: 'audit-packets',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, clinicalReportGuard],
    loadComponent: () => import('./reports/pages/audit-packet-builder.page').then(m => m.AuditPacketBuilderPage),
  },
  {
    path: 'profitability',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, profitabilityGuard],
    loadComponent: () => import('./features/care-routing/profitability-dashboard.component').then(m => m.ProfitabilityDashboardComponent),
  },
  { path: 'admin/super-admin', pathMatch: 'full', redirectTo: 'super-admin' },
  { path: 'admin/superadmin', pathMatch: 'full', redirectTo: 'super-admin' },
  { path: 'superadmin', pathMatch: 'full', redirectTo: 'super-admin' },
  { path: 'super-admin', canActivate: [authGuard, internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./super-admin/super-admin.module').then(m => m.SuperAdminModule) },
  { path: 'admin', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./admin/admin.module').then(m => m.AdminModule) },
  { path: 'nurse', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./nurse/nurse.module').then(m => m.NurseModule) },
  { path: 'finance', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/finance/finance.module').then(m => m.FinanceModule) },
  { path: 'housekeeping', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/housekeeping/housekeeping.module').then(m => m.HousekeepingModule) },
  { path: 'laundry', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/laundry/laundry.module').then(m => m.LaundryModule) },
  { path: 'reports', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/reports/reports.module').then(m => m.ReportsModule) },
  { path: 'provider', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/provider/provider.module').then(m => m.ProviderModule) },
  { path: 'surveyor', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./surveyor/surveyor.module').then(m => m.SurveyorModule) },
  { path: 'wound-rounds', canActivate: [internalWorkspaceGuard, mfaGuard], loadChildren: () => import('./modules/wound-round/wound-round.module').then(m => m.WoundRoundModule) },
  {
    path: 'scheduling',
    canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, operationalWorkflowGuard],
    children: [
      { path: '', pathMatch: 'full', loadComponent: () => import('./scheduling/pages/scheduling.page').then(m => m.SchedulingPage), data: { scope: 'auto' as const } },
      { path: 'mine', loadComponent: () => import('./scheduling/pages/scheduling.page').then(m => m.SchedulingPage), data: { scope: 'mine' as const } },
      { path: 'hr', canActivate: [schedulerGuard], loadComponent: () => import('./scheduling/pages/scheduling.page').then(m => m.SchedulingPage), data: { scope: 'all' as const } },
    ],
  },
  {
    path: 'messages', canActivate: [authGuard, mfaGuard], children: [
      { path: '', pathMatch: 'full', loadComponent: () => import('./shared/messages/message.page').then(m => m.MessagePage), data: { folder: 'inbox' as const } },
    ],
  },
  {
    path: 'tasks', canActivate: [authGuard, internalWorkspaceGuard, mfaGuard, operationalWorkflowGuard], children: [
      { path: '', pathMatch: 'full', loadComponent: () => import('./shared/tasks/patient-tasks.component').then(m => m.TasksPageComponent), data: {} },
    ],
  }
];

const isolatedRoutes: Routes = routes.map(route=>['partner','login','session-expired','forbidden',''].includes(route.path || '') || route.path?.startsWith('mfa/') || route.redirectTo ? route :
  {...route,canActivate:[partnerIsolationGuard,...(route.canActivate || [])]});
@NgModule({ imports: [RouterModule.forRoot(isolatedRoutes)], exports: [RouterModule] })
export class AppRoutingModule { }
